// Season editor form sections: General, Dates, Irrigation system, Contract
// dates (the Advance section is SeasonAdvanceSection.jsx). Values may be
// MULTIPLE_VALUES in bulk edit; option lists come from the same Redux
// dictionaries the V1 UpdateFieldModal / KCBox use (soil_data, crop_data,
// farming_type_data). `isModerator` applies Edit Field's moderator rules to
// the inputs it gates: field size disabled, irrigation extras and the
// installation date hidden, the crop dates KCBox disables.
const React = require('react');
const { useMemo } = require('react');
const { useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const dayjs = require('../../utils/dayjsConfig');
const { upper } = require('helpers');
const { unitDisplayName } = require('unitsManager');
const {
  defaultRecommendationDisplay,
  recommendationDisplay,
} = require('fieldManager');
const {
  MULTIPLE_VALUES,
  NO_IRRIGATION_SYSTEM,
  irrigationRateMin,
} = require('./seasonHelpers');
const {
  IRRIGATION_TYPE_OPTIONS,
  TANK_TYPE_OPTIONS,
  CLUSTER_IDS,
  N_DRIP_IRRIGATION_TYPE,
} = require('./newSeasonConstants');

const DATE_INPUT_FORMAT = 'YYYY-MM-DD';

const isMultiple = (value) => value === MULTIPLE_VALUES;

const toDateInput = (value, isUnix) => {
  if (!value || isMultiple(value)) {
    return '';
  }
  const parsed = isUnix ? dayjs.unix(value) : dayjs(value);
  return parsed.isValid() ? parsed.format(DATE_INPUT_FORMAT) : '';
};

// `fixedWidth` keeps a section's inputs at a fixed 400px (the form fields);
// without it the body fills the row (the Map).
const Section = ({ title, fixedWidth = false, className = '', children }) => (
  <div className={'ns-section ' + className}>
    <div className='ns-section-title'>{title}</div>
    <div
      className={
        'ns-section-body' + (fixedWidth ? ' ns-section-body-fixed' : '')
      }
    >
      {children}
    </div>
  </div>
);

// `error`: the field's validation message (seasonHelpers.validateSeasonValues).
const Row = ({ label, error, children }) => (
  <div className='ns-form-row'>
    <label className='ns-form-label'>{label}</label>
    <div className='ns-form-input'>
      {children}
      {error && <div className='ns-error-text'>{error}</div>}
    </div>
  </div>
);

// {key: error id} -> key => translated message (or undefined).
const useErrorText = (errors, params) => {
  const { t } = useTranslation();
  return (key) => {
    const error = errors?.[key];
    if (!error) return undefined;
    return typeof error === 'string'
      ? t(error, params)
      : t(error.id, { ...params, ...error.params });
  };
};

// options: [{value, label}]. A current value missing from the list is kept as
// an option so the select never silently shows a different value.
const OptionSelect = ({ value, options, onChange, disabled, emptyLabel }) => {
  const { t } = useTranslation();
  const hasValue =
    value === '' || isMultiple(value) || options.some((o) => o.value === value);
  return (
    <select
      className='form-control custom-select ns-input'
      value={value ?? ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      {isMultiple(value) && (
        <option value={MULTIPLE_VALUES}>
          {t('new_season_multiple_values')}
        </option>
      )}
      <option value=''>{emptyLabel ?? t('new_season_select')}</option>
      {!hasValue && <option value={value}>{value}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const keysAsOptions = (object, t) =>
  Object.keys(object || {})
    .sort()
    .map((key) => ({ value: key, label: t(upper(key)) }));

const useOptionDictionaries = () => {
  const soilData = useSelector(
    (state) => state.soil_data?.soil_data?.soil_data,
  );
  const cropData = useSelector(
    (state) => state.crop_data?.crop_data?.crop_data,
  );
  const farmingTypes = useSelector(
    (state) => state.farming_type_data?.farming_type_data,
  );
  return { soilData, cropData, farmingTypes };
};

const soilOptions = (soilData, t) =>
  Object.keys(soilData || {})
    .filter((key) => soilData[key].active === true)
    .sort()
    .map((key) => ({ value: key, label: t(upper(key)) }));

// Stored soil types may differ in case from the dictionary keys (V1 compares
// lower-cased); show the matching key.
const matchSoilKey = (value, options) => {
  if (!value || isMultiple(value)) {
    return value;
  }
  const match = options.find(
    (option) => option.value.toLowerCase() === String(value).toLowerCase(),
  );
  return match ? match.value : value;
};

const firstKey = (object) => Object.keys(object || {})[0] ?? '';

const clusterOptions = CLUSTER_IDS.map((id) => ({ value: id, label: id }));

// A number input that keeps '' for an empty or invalid entry.
const NumberInput = ({ value, onChange, disabled, ...rest }) => {
  const { t } = useTranslation();
  return (
    <input
      type='number'
      step='any'
      className='form-control ns-input'
      value={isMultiple(value) ? '' : (value ?? '')}
      placeholder={isMultiple(value) ? t('new_season_multiple_values') : ''}
      disabled={disabled}
      onChange={(event) => {
        const parsed = parseFloat(event.target.value);
        onChange(isNaN(parsed) ? '' : parsed);
      }}
      {...rest}
    />
  );
};

// `polygonArea`: the selected season's polygon area in the user's unit, which
// Edit Field shows when no field size is stored.
const GeneralSection = ({
  values,
  errors,
  onChange,
  onChangeMany,
  isModerator,
  polygonArea,
}) => {
  const { t } = useTranslation();
  const errorOf = useErrorText(errors);
  const areaUnit = useSelector(
    (state) => state.authentication_data?.user_data?.area,
  );
  const { soilData, cropData, farmingTypes } = useOptionDictionaries();
  const soils = useMemo(() => soilOptions(soilData, t), [soilData, t]);
  const varieties = cropData?.[values.crop_type];
  const features = varieties?.[values.crop_variety];
  const farmingTypeOptions = Object.keys(farmingTypes || {}).map((key) => ({
    value: key,
    label: farmingTypes[key],
  }));
  // Same cascade as V1 KCBox: a new crop type picks its first variety and
  // feature; a new variety picks its first feature.
  const onCropType = (cropType) => {
    const variety = firstKey(cropData?.[cropType]);
    onChangeMany({
      crop_type: cropType,
      crop_variety: variety,
      crop_feature: firstKey(cropData?.[cropType]?.[variety]),
    });
  };
  const onVariety = (variety) =>
    onChangeMany({
      crop_variety: variety,
      crop_feature: firstKey(varieties?.[variety]),
    });

  return (
    <Section title={t('new_season_section_general')} fixedWidth>
      <Row label={t('soil_type')} error={errorOf('soil_type')}>
        <OptionSelect
          value={matchSoilKey(values.soil_type, soils)}
          options={soils}
          onChange={(v) => onChange('soil_type', v)}
        />
      </Row>
      <Row label={t('crop_type')} error={errorOf('crop_type')}>
        <OptionSelect
          value={values.crop_type}
          options={keysAsOptions(cropData, t)}
          onChange={onCropType}
        />
      </Row>
      <Row label={t('kc_variety')}>
        <OptionSelect
          value={values.crop_variety}
          options={keysAsOptions(varieties, t)}
          disabled={isMultiple(values.crop_type)}
          onChange={onVariety}
        />
      </Row>
      <Row label={t('kc_feature')}>
        <OptionSelect
          value={values.crop_feature}
          options={keysAsOptions(features, (key) => key)}
          disabled={
            isMultiple(values.crop_type) || isMultiple(values.crop_variety)
          }
          onChange={(v) => onChange('crop_feature', v)}
        />
      </Row>
      <Row label={t('farming_type')} error={errorOf('farming_type')}>
        <OptionSelect
          value={values.farming_type}
          options={farmingTypeOptions}
          onChange={(v) => onChange('farming_type', v)}
        />
      </Row>
      <Row
        label={t('field_size') + ` (${unitDisplayName(areaUnit)})`}
        error={errorOf('area_size')}
      >
        <NumberInput
          value={
            values.area_size === '' ? (polygonArea ?? '') : values.area_size
          }
          disabled={isModerator}
          onChange={(v) => onChange('area_size', v)}
        />
      </Row>
      <Row label={t('cluster')}>
        <OptionSelect
          value={values.cluster}
          options={clusterOptions}
          emptyLabel={t('none_default')}
          onChange={(v) => onChange('cluster', v)}
        />
      </Row>
    </Section>
  );
};

const DateInput = ({ value, isUnix, onChange, disabled }) => {
  const { t } = useTranslation();
  return (
    <input
      type='date'
      className='form-control ns-input'
      disabled={disabled}
      value={toDateInput(value, isUnix)}
      placeholder={isMultiple(value) ? t('new_season_multiple_values') : ''}
      title={isMultiple(value) ? t('new_season_multiple_values') : undefined}
      onChange={(event) => {
        const text = event.target.value;
        if (!text) {
          onChange(isUnix ? null : '');
          return;
        }
        onChange(
          isUnix ? dayjs(text).unix() : dayjs(text).format(DATE_INPUT_FORMAT),
        );
      }}
    />
  );
};

// A date row with the "Multiple values" hint under it.
const DateRow = ({ label, error, value, onChange, disabled, isUnix }) => {
  const { t } = useTranslation();
  return (
    <Row label={label} error={error}>
      <DateInput
        value={value}
        isUnix={isUnix}
        disabled={disabled}
        onChange={onChange}
      />
      {isMultiple(value) && (
        <div className='ns-muted'>{t('new_season_multiple_values')}</div>
      )}
    </Row>
  );
};

const DatesSection = ({ values, errors, onChange, isModerator }) => {
  const { t } = useTranslation();
  const errorOf = useErrorText(errors);
  return (
    <Section title={t('new_season_section_dates')} fixedWidth>
      <Row
        error={errorOf('sow_date')}
        label={
          <span>
            {t('new_season_sow')}{' '}
            <span className='ns-info' title={t('new_season_sow_info')}>
              ⓘ
            </span>
          </span>
        }
      >
        <DateInput
          value={values.sow_date}
          onChange={(v) => onChange('sow_date', v)}
        />
        {isMultiple(values.sow_date) && (
          <div className='ns-muted'>{t('new_season_multiple_values')}</div>
        )}
      </Row>
      {/* KCBox: an unset irrigation start shows (and checks) as the sow date. */}
      <DateRow
        label={t('edit_field_pre_season_irrigation_start_date')}
        error={errorOf('irrigation_start_date')}
        value={values.irrigation_start_date || values.sow_date}
        disabled={isModerator}
        onChange={(v) => onChange('irrigation_start_date', v)}
      />
      <DateRow
        label={t('kc_harvest_date')}
        error={errorOf('harvest_date')}
        value={values.harvest_date}
        disabled={isModerator}
        onChange={(v) => onChange('harvest_date', v)}
      />
    </Section>
  );
};

const irrigationOptions = IRRIGATION_TYPE_OPTIONS.map((option) => ({
  value: option.value,
  label: option.title,
}));

const tankTypeOptions = TANK_TYPE_OPTIONS.map((option) => ({
  value: option.value,
  label: option.title,
}));

// IrrigationSystem.jsx: how the recommendation is shown, for any type with an
// irrigation system. Unset follows the type's default.
const RecommendationDisplayRow = ({ values, onChange }) => {
  const { t } = useTranslation();
  const type = values.irrigation_type;
  if (!type || type === NO_IRRIGATION_SYSTEM) {
    return null;
  }
  const value =
    isMultiple(type) || isMultiple(values.recommendation_display)
      ? values.recommendation_display
      : values.recommendation_display || defaultRecommendationDisplay(type);
  const options = [
    {
      value: recommendationDisplay.DURATION,
      label: t('recommendation_display_duration'),
    },
    {
      value: recommendationDisplay.AMOUNT,
      label: t('recommendation_display_amount'),
    },
  ];
  return (
    <Row label={t('edit_field_recommendation_display')}>
      <OptionSelect
        value={value}
        options={options}
        onChange={(v) => onChange('recommendation_display', v)}
      />
      <div className='ns-muted'>
        {t('edit_field_recommendation_display_description')}
      </div>
    </Row>
  );
};

// IrrigationSystem.jsx, N-Drip only: tank type, designed pressure, the
// measured rate (read only) and whether it is the representative input.
const NDripRows = ({ values, onChange, unit, measuredRate }) => {
  const { t } = useTranslation();
  return (
    <>
      <Row label={t('edit_field_tank_type')}>
        <OptionSelect
          value={values.tank_type}
          options={tankTypeOptions}
          onChange={(v) => onChange('tank_type', v)}
        />
      </Row>
      <Row label={`${t('designed_pressure')} (${unit})`}>
        <NumberInput
          value={values.irrigation_hin || ''}
          placeholder='0.0'
          onChange={(v) => onChange('irrigation_hin', v)}
        />
      </Row>
      <Row label={`${t('edit_field_measured_irrigation_rate')} (${unit}/h)`}>
        <input
          type='number'
          className='form-control ns-input'
          value={measuredRate ?? ''}
          disabled
          readOnly
        />
      </Row>
      <label className='ns-checkbox-row'>
        <input
          type='checkbox'
          checked={values.is_measured_rate_active === true}
          ref={(input) => {
            if (input) {
              input.indeterminate = isMultiple(values.is_measured_rate_active);
            }
          }}
          onChange={(event) =>
            onChange('is_measured_rate_active', event.target.checked)
          }
        />
        <span>{t('edit_field_representative_input_checkbox')}</span>
      </label>
    </>
  );
};

const IrrigationSection = ({
  values,
  errors,
  onChange,
  onChangeMany,
  isModerator,
  measuredRate,
}) => {
  const { t } = useTranslation();
  const userData = useSelector((state) => state.authentication_data?.user_data);
  const unit = userData?.length ? unitDisplayName(userData.length) : '';
  const rate = values.irrigation_rate;
  const errorOf = useErrorText(errors, {
    min: irrigationRateMin(userData?.length),
  });
  // UpdateFieldModal.handleIrrigationSystemTypeChange: a new type resets how
  // the recommendation is shown to that type's default.
  const onType = (type) =>
    onChangeMany({
      irrigation_type: type,
      recommendation_display: defaultRecommendationDisplay(type),
    });
  return (
    <Section title={t('new_season_section_irrigation')} fixedWidth>
      <Row label={t('irrigation_type')} error={errorOf('irrigation_type')}>
        <OptionSelect
          value={values.irrigation_type}
          options={irrigationOptions}
          onChange={onType}
        />
      </Row>
      {/* IrrigationSystem.jsx: no rate without an irrigation system. */}
      {values.irrigation_type !== NO_IRRIGATION_SYSTEM && (
        <Row
          label={t('new_season_irrigation_rate_label', { unit })}
          error={errorOf('irrigation_rate')}
        >
          <input
            type='number'
            step='any'
            min={irrigationRateMin(userData?.length)}
            className='form-control ns-input'
            value={isMultiple(rate) ? '' : (rate ?? '')}
            placeholder={
              isMultiple(rate) ? t('new_season_multiple_values') : ''
            }
            onChange={(event) => {
              const parsed = parseFloat(event.target.value);
              onChange('irrigation_rate', isNaN(parsed) ? '' : parsed);
            }}
          />
        </Row>
      )}
      {/* Edit Field hides these irrigation settings from moderators. */}
      {!isModerator && (
        <RecommendationDisplayRow values={values} onChange={onChange} />
      )}
      {!isModerator && values.irrigation_type === N_DRIP_IRRIGATION_TYPE && (
        <NDripRows
          values={values}
          onChange={onChange}
          unit={unit}
          measuredRate={measuredRate}
        />
      )}
    </Section>
  );
};

// `installationDate`: the selected seasons' contract installation date
// (MULTIPLE_VALUES when they differ). Read only, as in Edit Field, and hidden
// from moderators.
const ContractSection = ({
  values,
  onChange,
  installationDate,
  isModerator,
}) => {
  const { t } = useTranslation();
  return (
    <Section title={t('new_season_section_contract')} fixedWidth>
      {!isModerator && (
        <DateRow
          label={t('edit_field_installation_date')}
          value={installationDate}
          isUnix
          disabled
          onChange={() => {}}
        />
      )}
      <Row label={t('edit_field_contract_duration')}>
        <DateInput
          value={values.expiration_date}
          isUnix
          onChange={(v) => onChange('expiration_date', v)}
        />
        {isMultiple(values.expiration_date) && (
          <div className='ns-muted'>{t('new_season_multiple_values')}</div>
        )}
      </Row>
    </Section>
  );
};

module.exports = {
  Section,
  GeneralSection,
  DatesSection,
  IrrigationSection,
  ContractSection,
};
